import { ascending } from 'd3-array'

import { REGIONS, REGION_LEVELS } from 'nswidgets/src/components/map/constants'
import ncCountyTopo from 'nswidgets/src/components/map/topology/nc-counties.json'
import usTopology from 'nswidgets/src/components/map/topology/us-states.json'
import globalTopology from 'nswidgets/src/components/map/topology/global-countries.json'
import stateLetterCodes from 'nswidgets/src/components/map/topology/us-states-letter-codes.json'
import countryLetterCodes from 'nswidgets/src/components/map/topology/global-country-mapping.json'
import countryNames from 'nswidgets/src/components/map/topology/global-country-full-name-mapping.json'

const COUNTRY_ABBR_TO_NAME = generateCountryAbbreviationToNameMap()
const STATE_ABBR_TO_NAME = generateStateAbbreviationToNameMap()
const FIPS_TO_COUNTY = generateFipsToCountyMap()

function generateStateAbbreviationToNameMap () {
  return mapGeoCodeToName(usTopology.objects.states.geometries, stateLetterCodes)
}

function generateCountryAbbreviationToNameMap () {
  // First get the names from the map data (give preference to this name if different from countryNames)
  const result = mapGeoCodeToName(globalTopology.objects.countries.geometries, countryLetterCodes)

  // Augment the list of codes/names in the map topology with a full list
  // This is to provide names in the table behind the map even when the country is not in the map data
  Object.keys(countryNames).forEach((key) => {
    if (!result[key]) {
      result[key] = countryNames[key]
    }
  })

  return result
}

function mapGeoCodeToName (geoCodes, letterCodes) {
  const result = {}

  for (const prop in letterCodes) {
    const thisGeo = geoCodes.find(({ id }) => (id === prop))
    result[letterCodes[prop]] = thisGeo.properties.name
  }

  return result
}

function generateFipsToCountyMap () {
  return ncCountyTopo.objects.ncmap.geometries.reduce((acc, { properties }) => {
    acc[properties.FIPS_integ] = properties.county
    return acc
  }, {})
}

class MapBlockService {
  transformToTableData (data, region, regionLevel, regionField, valueField, valueLabel, { value2Field, value2Label, referenceData, thresholdFilters }) {
    const isNcCountyLevel = region === REGIONS.NC && this.isCountyLevel(regionLevel)
    const isUsMap = region === REGIONS.US
    const isGlobalMap = region === REGIONS.GLOBAL

    const valueColumn = data.map((d) => {
      let row = d[regionField].toString()
      let namedRow

      if (isNcCountyLevel) {
        namedRow = FIPS_TO_COUNTY[row]
      } else if (isUsMap) {
        namedRow = STATE_ABBR_TO_NAME[row]
      } else if (isGlobalMap) {
        namedRow = COUNTRY_ABBR_TO_NAME[row]
      }

      const dataPoint = {
        row: namedRow || row, // for regions like Washington D.C., if it is in the data but not in our name map
        column: valueLabel,
        value: d[valueField],
        cell_format: 'value'
      }
      return dataPoint
    })

    let value2Column = []
    if (value2Field && value2Label) {
      value2Column = data.map((d, index) => {
        const dataPoint = {
          row: valueColumn[index].row,
          column: value2Label,
          value: d[value2Field],
          cell_format: 'value2'
        }
        return dataPoint
      })
    }

    const refData = []
    if (referenceData && (Object.keys(referenceData).length > 0) && thresholdFilters && thresholdFilters.fields && thresholdFilters.fields[0].options) {
      const filterOptions = thresholdFilters.fields[0].options
      filterOptions.forEach(({ value, label }) => {
        const refDataColumn = referenceData[value]
        valueColumn.forEach(({ row }) => {
          refData.push({
            row,
            column: label,
            value: refDataColumn[row],
            cell_format: 'refData'
          })
        })
      })
    }

    return valueColumn
      .concat(value2Column)
      .concat(refData)
      .sort((value1, value2) => {
        return ascending(value1.row, value2.row)
      })
  }

  isCountyLevel (regionLevel) {
    return (regionLevel === REGION_LEVELS.COUNTY) || (regionLevel === undefined)
  }

  getTablePrimaryLabel (region, regionLevel) {
    if (region === REGIONS.US) {
      return 'State'
    }

    if (region === REGIONS.GLOBAL) {
      return 'Country'
    }

    if (this.isCountyLevel(regionLevel)) {
      return 'County'
    } else {
      return 'Zip Code'
    }
  }

  getTableCellFormat (valueType, value2Type) {
    return {
      value: [{
        format: 'number',
        type: valueType || 'number',
        align: 'center',
        variable: 'value'
      }],
      value2: [{
        format: 'number',
        type: value2Type || 'number',
        align: 'center',
        variable: 'value'
      }],
      refData: [{
        format: 'number',
        type: 'percent_shorter',
        align: 'center',
        variable: 'value'
      }]
    }
  }
}

export const mapBlockService = new MapBlockService()
